import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

export type Severity = "success" | "info" | "warning" | "error";

export interface AppNotification {
  id: string;
  ts: number;
  severity: Severity;
  title: string;
  description?: string;
  context?: string;          // free-form: "Importar Correos Express", "Sync Order Desk"…
  items?: Array<{ label: string; detail?: string }>; // optional detailed list (per row)
  read: boolean;
}

interface PushOpts {
  severity: Severity;
  title: string;
  description?: string;
  context?: string;
  items?: Array<{ label: string; detail?: string }>;
}

interface NotificationStoreShape {
  notifications: AppNotification[];
  unreadCount: number;
  push: (opts: PushOpts) => string;
  markRead: (id: string) => void;
  markAllRead: () => void;
  remove: (id: string) => void;
  clearAll: () => void;
}

const NotificationContext = createContext<NotificationStoreShape | null>(null);

const STORAGE_KEY = "nannuk_notifications_v1";
const MAX_KEEP = 200;
// Las notificaciones de BD (tabla user_notifications) se mezclan con las
// locales; se distinguen por este prefijo en el id para sincronizar
// leído/borrado contra el servidor.
const DB_PREFIX = "db:";

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [localNotifications, setLocalNotifications] = useState<AppNotification[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.slice(0, MAX_KEEP);
    } catch {
      return [];
    }
  });
  const [dbNotifications, setDbNotifications] = useState<AppNotification[]>([]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(localNotifications.slice(0, MAX_KEEP)));
    } catch { /* quota / disabled */ }
  }, [localNotifications]);

  const fetchDbNotifications = useCallback(async () => {
    try {
      const { data } = await (supabase as any)
        .from("user_notifications")
        .select("id, severity, title, description, context, read, created_at")
        .order("created_at", { ascending: false })
        .limit(50);
      if (!Array.isArray(data)) return;
      setDbNotifications(
        data.map((r: any): AppNotification => ({
          id: DB_PREFIX + r.id,
          ts: Date.parse(r.created_at),
          severity: (r.severity as Severity) || "info",
          title: r.title,
          description: r.description ?? undefined,
          context: r.context ?? undefined,
          read: !!r.read,
        })),
      );
    } catch { /* sin sesión: nada que mostrar */ }
  }, []);

  useEffect(() => {
    fetchDbNotifications();
    const interval = setInterval(fetchDbNotifications, 60_000);
    const { data: authSub } = supabase.auth.onAuthStateChange(() => {
      fetchDbNotifications();
    });
    return () => {
      clearInterval(interval);
      authSub.subscription.unsubscribe();
    };
  }, [fetchDbNotifications]);

  const push = useCallback((opts: PushOpts): string => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    // El panel de avisos persiste solo cosas que requieren atención.
    // success/info se quedan en los toasts transitorios y, si aplica, en audit_log.
    if (opts.severity !== "warning" && opts.severity !== "error") {
      return id;
    }
    const n: AppNotification = {
      id,
      ts: Date.now(),
      read: false,
      ...opts,
    };
    setLocalNotifications((prev) => [n, ...prev].slice(0, MAX_KEEP));
    return id;
  }, []);

  const markRead = useCallback((id: string) => {
    if (id.startsWith(DB_PREFIX)) {
      setDbNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      (supabase as any).from("user_notifications").update({ read: true }).eq("id", id.slice(DB_PREFIX.length)).then(() => {});
      return;
    }
    setLocalNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }, []);

  const markAllRead = useCallback(() => {
    setLocalNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setDbNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    (supabase as any).from("user_notifications").update({ read: true }).eq("read", false).then(() => {});
  }, []);

  const remove = useCallback((id: string) => {
    if (id.startsWith(DB_PREFIX)) {
      setDbNotifications((prev) => prev.filter((n) => n.id !== id));
      (supabase as any).from("user_notifications").delete().eq("id", id.slice(DB_PREFIX.length)).then(() => {});
      return;
    }
    setLocalNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setLocalNotifications([]);
    setDbNotifications([]);
    (supabase as any).from("user_notifications").delete().gte("created_at", "1970-01-01").then(() => {});
  }, []);

  const notifications = useMemo(
    () => [...localNotifications, ...dbNotifications].sort((a, b) => b.ts - a.ts),
    [localNotifications, dbNotifications],
  );

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  const value: NotificationStoreShape = useMemo(
    () => ({ notifications, unreadCount, push, markRead, markAllRead, remove, clearAll }),
    [notifications, unreadCount, push, markRead, markAllRead, remove, clearAll],
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications(): NotificationStoreShape {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications must be used inside <NotificationProvider>");
  return ctx;
}

// Safe variant: returns null instead of throwing. Useful for modules that may
// be rendered outside the provider (e.g. tests, isolated previews).
export function useNotificationsSafe(): NotificationStoreShape | null {
  return useContext(NotificationContext);
}
